import {
	ParsedTraceWorksheet,
	TraceWorksheetHeader,
	TraceSection,
	AlgorithmExecution,
	ActivityLine,
	ActivityType,
	TraceWorksheetStats,
	ConditionDetail,
	SetDetail,
	RateKeyLookupDetail,
	RateKeyResultDetail,
	FormDetail,
	ArithmeticDetail,
	EvaluationDetail
} from '@/models/TraceWorksheetModels';

const SECTION_REGEX = /^Executing (.+) algorithms\.\s*Type Activity:\s*(\w+)\s*\|\s*Issue Code:\s*(\w+)/;
const ALGO_START_REGEX = /^Executing algo (\d+) of (\d+):\s+(\d+)\s+(.+?)\s+(\S+_\S+_\S+)$/;
const ALGO_CONTEXT_REGEX = /^Algorithm\s+(\d+)\s+(.+?)\s+(\S+_\S+_\S+)$/;

function parseAlgorithmIdentifier(identifier: string): {
	lob: string; insuranceLine: string; productCode: string;
	coverageCode: string; rateBook: string; shortName: string;
} {
	const parts = identifier.split('_');
	if (parts.length >= 6) {
		return {
			lob: parts[0],
			insuranceLine: parts[1],
			productCode: parts[2],
			coverageCode: parts[3],
			rateBook: parts[4],
			shortName: parts.slice(5).join('_'),
		};
	}
	return {
		lob: '', insuranceLine: '', productCode: '',
		coverageCode: '', rateBook: '', shortName: identifier,
	};
}

function parseContext(line: string): {
	state: string | null; line: string | null;
	product: string | null; unit: string | null; coverage: string | null;
} {
	const result = { state: null as string | null, line: null as string | null, product: null as string | null, unit: null as string | null, coverage: null as string | null };

	const stateMatch = line.match(/State:\s*(\S+)/);
	if (stateMatch) result.state = stateMatch[1];

	const lineMatch = line.match(/Line:\s*(\S+)/);
	if (lineMatch) result.line = lineMatch[1];

	const productMatch = line.match(/Product:\s*(.+?)(?:\s*\||$)/);
	if (productMatch) result.product = productMatch[1].trim();

	const unitMatch = line.match(/Unit:\s*(.+?)(?:\s*\||$)/);
	if (unitMatch) result.unit = unitMatch[1].trim();

	const coverageMatch = line.match(/Coverage:\s*(.+?)(?:\s*\||$)/);
	if (coverageMatch) result.coverage = coverageMatch[1].trim();

	return result;
}

function parseCondition(text: string): ConditionDetail | undefined {
	// Pattern: "ATTRIBUTE_NAME actualValue operator *expectedLabel expectedValue = True/False"
	// Examples:
	//   "BC TYPE ACTIVITY NB != *RB RB = True"
	//   "POLICY USER CODE 1 CPP/WO == *CPP CPP = False"
	//   "POLICY EFFECTIVE DTE 1260210 < *1111205 1111205 = False"
	//   "CA LIABILITY LIMIT 1000000 == *BLANKS  = False" (no expected value after label)
	const match = text.match(/^(.+?)\s+([\S]+)\s+(==|!=|<|>|<=|>=)\s+\*(\S+)\s+(\S+)\s+=\s+(True|False)$/);
	if (match) {
		return {
			attribute: match[1].trim(),
			actualValue: match[2],
			operator: match[3],
			expectedValue: match[4],
			result: match[6] === 'True'
		};
	}
	// Variant with no expected value (e.g., *BLANKS represents empty)
	const match2 = text.match(/^(.+?)\s+([\S]+)\s+(==|!=|<|>|<=|>=)\s+\*(\S+)\s+=\s+(True|False)$/);
	if (match2) {
		return {
			attribute: match2[1].trim(),
			actualValue: match2[2],
			operator: match2[3],
			expectedValue: match2[4],
			result: match2[5] === 'True'
		};
	}
	return undefined;
}

function parseSet(text: string): SetDetail | undefined {
	// "Set Attribute POLICY USER CODE 8 to 1.00"
	// "Set NamedRatingElement AUDIT FINAL to "
	const match = text.match(/^Set (Attribute|NamedRatingElement)\s+(.+?)\s+to\s*(.*)$/);
	if (match) {
		return {
			targetType: match[1] as 'Attribute' | 'NamedRatingElement',
			target: match[2].trim(),
			value: match[3].trim() || '(empty)'
		};
	}
	// "Set RESULT01274 to " (no Attribute/NamedRatingElement keyword)
	const match2 = text.match(/^Set\s+(.+?)\s+to\s*(.*)$/);
	if (match2) {
		return {
			targetType: 'Attribute',
			target: match2[1].trim(),
			value: match2[2].trim() || '(empty)'
		};
	}
	return undefined;
}

function parseRateKeyLookup(text: string): RateKeyLookupDetail | undefined {
	// "Rate Key Selected: MOE OR POL SIZE CNVT, Effective 2/26/2024, State US, LOC/MCO/PCO 99/99/99, LOB CPP, Line AL, Product ALL, Coverage ALL, RateBook A"
	const match = text.match(/^Rate Key Selected:\s*(.+?),\s*Effective\s+(\S+),\s*State\s+(\S+),\s*LOC\/MCO\/PCO\s+(\S+),\s*LOB\s+(\S+),\s*Line\s+(\S+),\s*Product\s+(\S+),\s*Coverage\s+(\S+),\s*RateBook\s+(\S+)$/);
	if (match) {
		return {
			keyName: match[1].trim(),
			effectiveDate: match[2],
			state: match[3],
			locMcoPco: match[4],
			lob: match[5],
			line: match[6],
			product: match[7],
			coverage: match[8],
			rateBook: match[9]
		};
	}
	return undefined;
}

function parseRateKeyResult(text: string): RateKeyResultDetail | undefined {
	// "Rate Key MOE OR POL SIZE CNVT: CPP/WO   1.00 found with result 1"
	// "Rate Key MAND FORM SEQ: 000000001 found with result 000000001"
	// "Rate Key POLICY MANDATORY FRM: 000000001 found with sequence values [ME0001    12504014, IL0028    11702014]"
	const match = text.match(/^Rate Key\s+(.+?):\s+(.+?)\s+found with (?:result|sequence values)\s*(.*)$/);
	if (match) {
		return {
			keyName: match[1].trim(),
			lookupValue: match[2].trim(),
			result: match[3].trim() || '(empty)'
		};
	}
	return undefined;
}

function parseForm(text: string): FormDetail | undefined {
	// "Form ME0001 1250401 Attached (loc 0, subloc 0, unit 0, code 4)"
	// "Processing form ME0001 1250401 (loc 0, subloc 0, unit 0, code 4)"
	const match = text.match(/^(?:Form|Processing form)\s+(\S+)\s+(\S+)\s+(Attached|Detached|Processing)?\s*\((.+)\)$/);
	if (match) {
		return {
			formNumber: match[1],
			formEdition: match[2],
			action: match[3] || 'Processing',
			location: match[4]
		};
	}
	// Simpler pattern without action
	const match2 = text.match(/^(?:Form|Processing form)\s+(\S+)\s+(\S+)\s+\((.+)\)$/);
	if (match2) {
		return {
			formNumber: match2[1],
			formEdition: match2[2],
			action: 'Processing',
			location: match2[3]
		};
	}
	return undefined;
}

function parseArithmetic(text: string): ArithmeticDetail | undefined {
	// Form A: "NO LIQ LIAB COV 3 + *1.00 1.00000 = 4"
	// Also: "CA LIABILITY RMF 1 * *HUNDRED 100.00000 = 100" (label after *)
	// Also: "TOTAL VEHICLES 0.00000 + *ONE 1.00000 = 1"
	const matchA = text.match(/^(.+?)\s+([\d.]+)\s+([+\-*/])\s+\*(\S+)\s+[\d.]+\s+=\s+([\d.-]+)$/);
	if (matchA) {
		const operandRaw = matchA[4];
		const numericPart = operandRaw.match(/^[\d.]+$/) ? operandRaw : operandRaw;
		return {
			attribute: matchA[1].trim(),
			currentValue: matchA[2],
			operator: matchA[3],
			operand: numericPart,
			result: matchA[5]
		};
	}
	// Form A with empty current value: "PPT POLL COUNT  + *1.00 1.00000 = 1"
	const matchA2 = text.match(/^(.+?)\s\s+([+\-*/])\s+\*(\S+)\s+[\d.]+\s+=\s+([\d.-]+)$/);
	if (matchA2) {
		return {
			attribute: matchA2[1].trim(),
			currentValue: '(empty)',
			operator: matchA2[2],
			operand: matchA2[3],
			result: matchA2[4]
		};
	}
	// Form A shorthand: "CA COST NEW / *1000 = 33"
	const matchA3 = text.match(/^(.+?)\s+([/])\s+\*(\S+)\s+=\s+([\d.-]+)$/);
	if (matchA3) {
		return {
			attribute: matchA3[1].trim(),
			currentValue: '',
			operator: matchA3[2],
			operand: matchA3[3],
			result: matchA3[4]
		};
	}
	// Form B: "PPT BASE COLL PREM 312.14558 * CA LOSS COST MULT 2.9474 = 920.01788"
	const matchB = text.match(/^(.+?)\s+([\d.-]+)\s+([+\-*/])\s+([A-Z][\w\s]*?)\s+([\d.-]+)\s+=\s+([\d.-]+)$/);
	if (matchB) {
		return {
			attribute: matchB[1].trim(),
			currentValue: matchB[2],
			operator: matchB[3],
			operandLabel: matchB[4].trim(),
			operand: matchB[5],
			result: matchB[6]
		};
	}
	// Form B with empty operand value: "PPT COLL PRM W/O RMF 198.04764 + PHY DAM EXP RTG PREM  = 198"
	const matchC = text.match(/^(.+?)\s+([\d.-]+)\s+([+\-*/])\s+([A-Z][\w\s]*?)\s+=\s+([\d.-]+)$/);
	if (matchC) {
		return {
			attribute: matchC[1].trim(),
			currentValue: matchC[2],
			operator: matchC[3],
			operandLabel: matchC[4].trim(),
			operand: '(empty)',
			result: matchC[5]
		};
	}
	// Form D: "PPT LIAB PRM W/O RMF / CA INCR LIAB LMT FTR = 758.15522" (no current value, division with label)
	const matchD = text.match(/^(.+?)\s+([/])\s+([A-Z][\w\s]*?)\s+=\s+([\d.-]+)$/);
	if (matchD) {
		return {
			attribute: matchD[1].trim(),
			currentValue: '',
			operator: matchD[2],
			operandLabel: matchD[3].trim(),
			operand: '',
			result: matchD[4]
		};
	}
	return undefined;
}

function parseEvaluation(text: string): EvaluationDetail | undefined {
	// "GU RATE STATE WA is AZ or WA and does qualify for Full Glass"
	// "GU RATE STATE CA is AZ or WA and does not qualify for Full Glass"
	const match = text.match(/^(.+?)\s+(\S+)\s+is\s+(.+?)\s+and\s+does\s+(not\s+)?qualify for\s+(.+)$/);
	if (match) {
		return {
			attribute: match[1].trim(),
			value: match[2],
			condition: match[3].trim(),
			qualifies: !match[4],
			target: match[5].trim()
		};
	}
	return undefined;
}

function classifyAndParse(text: string): { type: ActivityType; detail: Partial<ActivityLine> } {
	if (text.startsWith('Set ')) {
		const setResult = parseSet(text);
		if (setResult) return { type: 'set', detail: { set: setResult } };
	}
	if (text.startsWith('Rate Key Selected:')) {
		return { type: 'rateKeyLookup', detail: { rateKeyLookup: parseRateKeyLookup(text) } };
	}
	if (text.match(/^Rate Key .+?:/)) {
		return { type: 'rateKeyResult', detail: { rateKeyResult: parseRateKeyResult(text) } };
	}
	if (text.startsWith('Form ') || text.startsWith('Processing form')) {
		return { type: 'form', detail: { form: parseForm(text) } };
	}
	if (text.match(/= True$/) || text.match(/= False$/)) {
		return { type: 'condition', detail: { condition: parseCondition(text) } };
	}
	{
		const arith = parseArithmetic(text);
		if (arith) return { type: 'arithmetic', detail: { arithmetic: arith } };
	}
	if (text.match(/\band\s+does\s+(?:not\s+)?qualify for\b/)) {
		return { type: 'evaluation', detail: { evaluation: parseEvaluation(text) } };
	}
	return { type: 'other', detail: {} };
}

export function parseTraceWorksheet(rawText: string): ParsedTraceWorksheet {
	const lines = rawText.split('\n').map(l => l.replace(/\r$/, ''));
	const header: TraceWorksheetHeader = {
		policyNumber: '', correlationId: '', effectiveDate: '',
		entryDate: '', environment: '', dllInfo: ''
	};

	const sections: TraceSection[] = [];
	let currentSection: TraceSection | null = null;
	let currentAlgo: AlgorithmExecution | null = null;
	let pendingAlgoStart: { index: number; total: number; algoNum: string; algoName: string; identifier: string } | null = null;

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];
		const trimmed = line.trim();
		if (trimmed === '') continue;

		// Parse header lines
		if (trimmed.startsWith('Policy number:')) {
			header.policyNumber = trimmed.substring('Policy number:'.length).trim();
			continue;
		}
		if (trimmed.startsWith('Correlation ID:')) {
			header.correlationId = trimmed.substring('Correlation ID:'.length).trim();
			continue;
		}
		if (trimmed.startsWith('Effective Date:')) {
			header.effectiveDate = trimmed.substring('Effective Date:'.length).trim();
			continue;
		}
		if (trimmed.startsWith('Entry Date:')) {
			header.entryDate = trimmed.substring('Entry Date:'.length).trim();
			continue;
		}
		if (trimmed.startsWith('Environment:')) {
			header.environment = trimmed.substring('Environment:'.length).trim();
			continue;
		}
		if (trimmed.match(/^[a-zA-Z]:\\/) && header.dllInfo === '') {
			header.dllInfo = trimmed;
			continue;
		}

		// Section header
		const sectionMatch = trimmed.match(SECTION_REGEX);
		if (sectionMatch) {
			if (currentAlgo && currentSection) {
				currentSection.algorithms.push(currentAlgo);
				currentAlgo = null;
			}
			if (currentSection) {
				sections.push(currentSection);
			}
			currentSection = {
				name: sectionMatch[1],
				typeActivity: sectionMatch[2],
				issueCode: sectionMatch[3],
				algorithms: []
			};
			pendingAlgoStart = null;
			continue;
		}

		// Algorithm start line
		const algoStartMatch = trimmed.match(ALGO_START_REGEX);
		if (algoStartMatch) {
			if (currentAlgo && currentSection) {
				currentSection.algorithms.push(currentAlgo);
				currentAlgo = null;
			}
			pendingAlgoStart = {
				index: parseInt(algoStartMatch[1]),
				total: parseInt(algoStartMatch[2]),
				algoNum: algoStartMatch[3],
				algoName: algoStartMatch[4].trim(),
				identifier: algoStartMatch[5]
			};
			continue;
		}

		// Algorithm context line
		const algoContextMatch = trimmed.match(ALGO_CONTEXT_REGEX);
		if (algoContextMatch) {
			// If there's an existing algorithm in progress, push it
			if (currentAlgo && currentSection) {
				currentSection.algorithms.push(currentAlgo);
			}

			const algoNum = algoContextMatch[1];
			const algoName = algoContextMatch[2].trim();
			const identifier = algoContextMatch[3];
			const idParts = parseAlgorithmIdentifier(identifier);

			// Look ahead for context line (State: ... | Line: ...)
			let contextLine = '';
			if (i + 1 < lines.length) {
				const nextTrimmed = lines[i + 1].trim();
				if (nextTrimmed.startsWith('State:')) {
					contextLine = nextTrimmed;
					i++; // consume the context line
				}
			}
			const ctx = parseContext(contextLine);

			const sectionName = currentSection?.name ?? '';

			if (pendingAlgoStart && pendingAlgoStart.algoNum === algoNum) {
				currentAlgo = {
					index: pendingAlgoStart.index,
					total: pendingAlgoStart.total,
					algorithmNumber: algoNum,
					algorithmName: algoName,
					identifier: identifier,
					state: ctx.state,
					line: ctx.line,
					product: ctx.product,
					unit: ctx.unit,
					coverage: ctx.coverage,
					...idParts,
					activities: [],
					sectionName: sectionName,
					lineNumber: i + 1
				};
				pendingAlgoStart = null;
			} else {
				// Continuation of same algorithm with different context (no new "Executing algo" line)
				currentAlgo = {
					index: currentAlgo?.index ?? 0,
					total: currentAlgo?.total ?? 0,
					algorithmNumber: algoNum,
					algorithmName: algoName,
					identifier: identifier,
					state: ctx.state,
					line: ctx.line,
					product: ctx.product,
					unit: ctx.unit,
					coverage: ctx.coverage,
					...idParts,
					activities: [],
					sectionName: sectionName,
					lineNumber: i + 1
				};
			}
			continue;
		}

		// Activity line within an algorithm
		if (currentAlgo && trimmed !== '') {
			const { type, detail } = classifyAndParse(trimmed);
			currentAlgo.activities.push({
				type,
				rawText: trimmed,
				lineNumber: i + 1,
				...detail
			});
		}
	}

	// Push final algorithm and section
	if (currentAlgo && currentSection) {
		currentSection.algorithms.push(currentAlgo);
	}
	if (currentSection) {
		sections.push(currentSection);
	}

	const allAlgorithms = sections.flatMap(s => s.algorithms);

	return { header, sections, allAlgorithms, rawLines: lines };
}

export function computeStats(parsed: ParsedTraceWorksheet): TraceWorksheetStats {
	const algos = parsed.allAlgorithms;
	const allActivities = algos.flatMap(a => a.activities);

	return {
		totalAlgorithms: algos.length,
		totalActivities: allActivities.length,
		distinctAlgorithmNames: [...new Set(algos.map(a => a.shortName))].sort(),
		distinctStates: [...new Set(algos.map(a => a.state).filter(Boolean) as string[])].sort(),
		distinctLinesOfBusiness: [...new Set(algos.map(a => a.lob).filter(Boolean))].sort(),
		distinctInsuranceLines: [...new Set(algos.map(a => a.insuranceLine).filter(s => s && s !== 'All'))].sort(),
		totalConditions: allActivities.filter(a => a.type === 'condition').length,
		totalSetOperations: allActivities.filter(a => a.type === 'set').length,
		totalRateKeyLookups: allActivities.filter(a => a.type === 'rateKeyLookup').length,
		totalFormOperations: allActivities.filter(a => a.type === 'form').length,
		totalArithmeticOperations: allActivities.filter(a => a.type === 'arithmetic').length,
		totalEvaluations: allActivities.filter(a => a.type === 'evaluation').length,
		sectionSummaries: parsed.sections.map(s => ({
			name: s.name,
			algorithmCount: s.algorithms.length,
		})),
	};
}
